import { LoadingOutlined } from "@ant-design/icons";
import { useCreate, useGetIdentity, useOne, useSelect } from "@refinedev/core";
import { FormInstance, Form, Card, Button } from "antd";
import { useEffect, useCallback, useState } from "react";
import { ILeadQuizData } from "interfaces/crm";
import { LEAD_URL, CRM_UPDATE_QUIZ, VENUE_URL } from "urls";
import { NotesInput } from "../NotesInput";
import Question, { OptionProps, QuestionProps } from "../Question";
import { LeadServices } from "../ServicesModal";
import { initializeFormValues } from "./initializeForm";
import { LeadQuizFlowQuestions } from "./LeadQuizFlowQuestions";
import { OMIT_QUESTIONS, quizEngine } from "./quizEngine";
import { IProductShowcase } from "interfaces/product";
import { IStaff } from "interfaces/staff";

const LeadQuizFlow = ({
    questions,
    setQuestions,
    form,
    close,
    lead,
    isCustomerData,
}: {
    questions: QuestionProps[];
    setQuestions: React.Dispatch<React.SetStateAction<QuestionProps[]>>;
    form: FormInstance<any>;
    close: any;
    lead: any;
    isCustomerData: boolean;
}) => {
    const [venueList, setVenueList] = useState<OptionProps[]>([]);
    const { data: user } = useGetIdentity<IStaff>();
    const { mutate: create, isSuccess } = useCreate();

    const {
        data: quizData,
        status: quizStatus,
        error: quizError,
    } = useOne<ILeadQuizData>({
        resource: LEAD_URL,
        id: `${lead}/get_quiz_data`,
    });

    const handleSubmit = useCallback(() => {
        const quizData = {
            lead,
            json_data: questions,
        };

        create({
            resource: CRM_UPDATE_QUIZ,
            values: { quiz_data: quizData },
        });

        if (isSuccess) {
            form.resetFields();
            close();
        }
    }, [questions, create, isSuccess, form, close, lead]);

    const { queryResult: venues, onSearch: onSearch } = useSelect<IProductShowcase>({
        resource: VENUE_URL,
        queryOptions: {
            enabled: !!user,
            onSuccess: useCallback((data) => {
                const formattedData: OptionProps[] = data?.data.map(
                    (item: { name: any; id: any }) => ({
                        title: item.name,
                        value: item.id,
                    }),
                )
                setVenueList(formattedData)
                LeadQuizFlowQuestions.find(
                    (question) => question.index === "venue_name"
                )!.options = formattedData
            }, []),
        },
        onSearch: (value: string) => [
            {
                field: 'search',
                operator: 'eq',
                value: value,
            },
        ],
    })

    useEffect(() => {
        if (quizStatus === "success" && quizData?.data) {
            const rawData = isCustomerData
                ? quizData.data.customer_json_data
                : quizData.data.json_data;

            if (rawData) {
                const withVenueOptions = rawData.map((q: QuestionProps) => ({
                    ...q,
                    ...(q.index === "venue_name" && { options: venueList }),
                }));

                setQuestions(withVenueOptions);
                initializeFormValues(withVenueOptions, form);
                return;
            }
        }

        const defaults = LeadQuizFlowQuestions
            .filter((q) => q.index && !OMIT_QUESTIONS.includes(q.index))
            .map((q) => ({
                ...q,
                answer: q.type === "multiQuestion"
                    ? Array.from({ length: q.repeatCount || 1 }, () =>
                        Array(q.subQuestions?.length || 0).fill("")
                    )
                    : q.answer,
                ...(q.index === "venue_name" && { options: venueList }),
            }));


        setQuestions(defaults);
    }, [quizStatus, quizData]);

    if (quizStatus === "loading") {
        return (
            <div className="flex justify-center items-center h-full">
                <LoadingOutlined />
            </div>
        );
    }

    if (quizStatus === "error") {
        console.error("Error fetching quiz data:", quizError);
        return (
            <div className="flex justify-center items-center h-full">
                <p>Error fetching quiz data</p>
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                    {questions.length === 1 && <Button>Next</Button>}
                </div>
                <div>{lead && <NotesInput leadId={lead} />}</div>
            </div>
            <Card>
                {questions.map((question, index) => (
                    <Question
                        key={index}
                        index={index.toString()}
                        question={question.question}
                        type={question.type}
                        options={question.options}
                        enableSearch={question.enableSearch}
                        onSearch={onSearch}
                        subQuestions={question.subQuestions}
                        picker={question.picker}
                        answer={question.answer}
                        info={question.info}
                        onChange={(value) =>
                            quizEngine.handleQuestionChange(
                                value, index, questions, setQuestions
                            )
                        }
                        onSubChange={(repeatIndex, subIndex, value) =>
                            quizEngine.handleSubQuestionChange(
                                repeatIndex, subIndex, value, index, questions, setQuestions
                            )
                        }
                        repeatCount={question.repeatCount}
                    />
                ))}
            </Card>
            {!isCustomerData && (
                <>
                    <div style={{ marginTop: 16 }}>
                        <LeadServices leadId={lead} />
                    </div>
                    <div>
                        <Button type="primary" onClick={handleSubmit}>
                            Submit
                        </Button>
                    </div>
                </>
            )}
        </Form>
    );
};

export default LeadQuizFlow;